import { useQuery } from '@tanstack/react-query';
import { match } from 'ts-pattern';

import { type PaidPlan, paidPlansOptions } from '@/entities/subscription';
import { formatShortDate } from '@/shared/lib/time-utils';

import { PickPlanCard } from './pick-plan/pick-plan-card';
import { PickPlanCardSkeleton } from './pick-plan/pick-plan-card-skeleton';

const EXPECTED_PLAN_COUNT = 2;

type PickPlanProps = {
    currentPlanCode?: string;
    currentPeriodEnd?: Date;
    nextPlanCode?: string | null;
    pendingPlanCode?: string | null;
    onSelectPlan: (code: string) => void;
};

export function PickPlan({
    currentPlanCode,
    currentPeriodEnd,
    nextPlanCode,
    pendingPlanCode,
    onSelectPlan,
}: PickPlanProps) {
    const { data: plans, isPending, isError } = useQuery(paidPlansOptions());

    if (isError) {
        return (
            <p className="text-sm text-muted-foreground">Unable to load plans. Try again later.</p>
        );
    }

    const hasCurrentPlan = currentPlanCode != null;
    const hasPendingChange = nextPlanCode != null && nextPlanCode !== currentPlanCode;
    const isCheckoutPending = pendingPlanCode != null;
    const startsAt = currentPeriodEnd ? formatShortDate(currentPeriodEnd) : undefined;

    const describe = (plan: PaidPlan) => {
        const isCurrent = hasCurrentPlan && plan.code === currentPlanCode;
        const isPendingTarget = hasPendingChange && plan.code === nextPlanCode;

        return match({ isPendingTarget, isCurrent, hasPendingChange })
            .with({ isPendingTarget: true }, () => ({
                actionLabel: startsAt ? `Starting ${startsAt}` : 'Scheduled',
                actionVariant: 'outline' as const,
                disabled: true,
                showCheck: true,
                isSelecting: false,
                showMostPopular: false,
            }))
            .with({ isCurrent: true, hasPendingChange: false }, () => ({
                actionLabel: 'Current Plan',
                actionVariant: 'outline' as const,
                disabled: true,
                showCheck: true,
                isSelecting: false,
                showMostPopular: false,
            }))
            .otherwise(() => ({
                actionLabel: 'Select Plan',
                actionVariant: (!hasCurrentPlan && !plan.mostPopular ? 'outline' : 'default') as
                    | 'default'
                    | 'outline',
                disabled: isCheckoutPending,
                showCheck: false,
                isSelecting: plan.code === pendingPlanCode,
                showMostPopular: plan.mostPopular && !isCurrent,
                caption: startsAt ? `starts ${startsAt}` : undefined,
            }));
    };

    // Below sm the column fits exactly two 16rem cards, so auto-fit strands the third.
    return (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(16rem,1fr))] gap-6 max-sm:grid-cols-1">
            {isPending
                ? Array.from({ length: EXPECTED_PLAN_COUNT }).map((_, i) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: count is static
                      <PickPlanCardSkeleton key={i} />
                  ))
                : plans.map((plan) => (
                      <PickPlanCard
                          key={plan.code}
                          plan={plan}
                          {...describe(plan)}
                          onSelect={() => onSelectPlan(plan.code)}
                      />
                  ))}
        </div>
    );
}
